import styled from "styled-components"

import { colors, spacing, Text } from "@minuthq/meatball-ui-react"

import { EmptyStateCard } from "src/components/EmptyState/EmptyStateCard"
import PortfolioCovered from "src/components/EmptyState/images/integration-analytics-covered.svg"
import { IntegrationIcon } from "src/components/Integrations/IntegrationIcon"
import { LinearProgressBar } from "src/components/ProgressBar/LinearProgressBar"
import { useFeatureAvailability } from "src/data/featureAvailability/logic/useFeatureAvailability"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHomesCount } from "src/data/organizations/queries/homeQueries"
import { getIntegrationsCoverageTotals } from "src/data/portfolioAnalytics/logic/portfolioCoverage"
import { useFetchIntegrationsCoveragePerIntegration } from "src/data/portfolioAnalytics/portfolioAnalyticsQueries"
import { TIntegrationsCoveragePerIntegrationData } from "src/data/portfolioAnalytics/types/portfolioAnalyticsTypes"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { DonutChart } from "src/ui/Graphs/DonutChart"
import { InternalLink } from "src/ui/Link/InternalLink"
import { MCard } from "src/ui/MCard/MCard"

import { IntegrationStatusSkeletion } from "./IntegrationStatusSkeletion"
import { IntegrationStatusUpgradeSection } from "./IntegrationStatusUpgradeSection"
import { NoIntegrations } from "./NoIntegrations"
import {
  IntegrationStatusContent,
  IntegrationStatusSection,
  StyledMCard,
} from "./sharedStyles"

export function PortfolioAnalyticsIntegrationsStatus() {
  const { t } = useTranslate()
  const { orgId } = useOrganization()

  const fetchIntegrationsCoveragePerIntegration =
    useFetchIntegrationsCoveragePerIntegration({
      orgId,
    })
  const { available: integrationsAvailable, required_plan_types } =
    useFeatureAvailability({
      feature: "integration",
    })

  if (
    fetchIntegrationsCoveragePerIntegration.isLoading ||
    !fetchIntegrationsCoveragePerIntegration.data
  ) {
    return <IntegrationStatusSkeletion />
  }

  const integrationsCoveragePerIntegration =
    fetchIntegrationsCoveragePerIntegration.data.analytics

  // Totals across all integrations
  const totals = getIntegrationsCoverageTotals(
    integrationsCoveragePerIntegration
  )

  const unlinkedUnits = totals.totalUnits - totals.linkedUnits

  if (!integrationsAvailable) {
    return (
      <IntegrationStatusUpgradeSection
        requiredPlan={required_plan_types?.[0]}
      />
    )
  }

  if (totals.totalUnits < 1) {
    return <NoIntegrations />
  }

  if (totals.totalUnits === totals.linkedUnits) {
    return (
      <EmptyStateCard
        image={PortfolioCovered}
        title={t(
          langKeys.portfolio_analytics_integrations_status_covered_title
        )}
        subtitle={t(
          langKeys.portfolio_analytics_integrations_status_covered_subtitle
        )}
      />
    )
  }

  return (
    <StyledMCard padding={spacing.M} boxShadow={false} border={true}>
      <Text variant="heading3">
        {t(langKeys.portfolio_analytics_integrations_status)}
      </Text>

      <IntegrationStatusContent>
        <IntegrationStatusSection>
          <Text variant="bodyS" color="secondary">
            {t(langKeys.portfolio_analytics_integrations_status_covered)}
          </Text>

          <DonutChart
            title={{
              text: `${totals.linkedUnits}/${totals.totalUnits}`,
            }}
            data={[
              {
                name: t(
                  langKeys.portfolio_analytics_integrations_status_with_minut
                ),
                y: totals.linkedUnits,
              },
              {
                name: t(
                  langKeys.portfolio_analytics_integrations_status_with_other
                ),
                y: unlinkedUnits,
              },
            ]}
            seriesColors={[colors.systemGood, colors.neutralDark2]}
            containerProps={{ style: { height: 300 } }}
            labelFormatter={function (this: Highcharts.Point): string {
              return `<span>
                <b>${this.y} ${t("unit_plural")}</b> - ${this.name}
              </span>`
            }}
          />
        </IntegrationStatusSection>
        <IntegrationStatusSection>
          <Text variant="bodyS" color="secondary">
            {t(langKeys.portfolio_analytics_integrations_status_linked)}
          </Text>
          <LinkedIntegrationsList>
            {integrationsCoveragePerIntegration.map((integrationGroup) =>
              integrationGroup.data.map((integrationData) => {
                return (
                  <LinkedIntegrationItem
                    key={integrationData.grouped_by}
                    data={integrationData}
                  />
                )
              })
            )}
          </LinkedIntegrationsList>
        </IntegrationStatusSection>
      </IntegrationStatusContent>
    </StyledMCard>
  )
}

function LinkedIntegrationItem({
  data,
}: {
  data: TIntegrationsCoveragePerIntegrationData
}) {
  const { t, langKeys } = useTranslate()
  const { linked_homes, total_integration_entities, ratio } = data.aggregation
  const integrationName = data.metadata.integration_name
  const { orgId } = useOrganization()
  const fetchHomesCount = useFetchHomesCount({
    orgId,
  })

  const homesCount = fetchHomesCount.data || 0

  return (
    <InternalLink
      to={Routes.OrgIntegration.location(data.grouped_by).pathname}
      underline="never"
    >
      <LinkedIntegrationsCard
        padding={spacing.M}
        boxShadow={false}
        border={true}
      >
        <IntegrationIcon icon={data.metadata.icon_url} />
        <IntegrationContent>
          <Text variant="heading3">{integrationName}</Text>
          <IntegrationStats>
            <StatsRow>
              <Text variant="bodyS" color="tertiary">
                {`${linked_homes}/${homesCount}`}{" "}
                {t(langKeys.integrations_homes_linked_text)}
              </Text>
              <Text variant="bodyS" color="tertiary">
                {t(langKeys.portfolio_analytics_integrations_status_in_pms, {
                  count: total_integration_entities,
                })}
              </Text>
            </StatsRow>
            <LinearProgressBar
              segments={[
                { value: ratio * 100, type: "good" },
                {
                  value: (1 - ratio) * 100,
                  type: "error",
                },
              ]}
            />
          </IntegrationStats>
        </IntegrationContent>
      </LinkedIntegrationsCard>
    </InternalLink>
  )
}

const LinkedIntegrationsCard = styled(MCard)`
  display: flex;
  gap: ${spacing.M};
`

const LinkedIntegrationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.XS};
`

const IntegrationContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const IntegrationStats = styled.div`
  display: grid;
  gap: ${spacing.XS3};
`

const StatsRow = styled.div`
  display: flex;
  justify-content: space-between;
`
